import { useState } from 'react';
import { X, Upload, FileText, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { apiClient } from '../../lib/api-client';
import { toast } from 'sonner';

interface UploadDischargeSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient: any;
  onSuccess: () => void;
}

export function UploadDischargeSummaryModal({ 
  isOpen, 
  onClose, 
  patient,
  onSuccess 
}: UploadDischargeSummaryModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  if (!isOpen) return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        toast.error('Please select a PDF file');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      
      await apiClient.upload(`/physician-assistants/discharge-summary/${patient.id}/upload-pdf`, selectedFile);

      toast.success('Discharge summary uploaded successfully');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Failed to upload discharge summary:', error);
      toast.error(
        error.response?.data?.detail ||
        error.message ||
        'Failed to upload discharge summary'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-primary">Upload Discharge Summary</h2>
              <p className="text-sm text-muted-foreground">{patient.name}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Patient Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">UMR No:</span>
                <p className="text-primary">{patient.umr_no}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Age:</span>
                <p className="text-primary">{patient.age} years</p>
              </div>
              <div>
                <span className="text-muted-foreground">Gender:</span>
                <p className="text-primary">{patient.gender}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Hospital:</span>
                <p className="text-primary">{patient.hospital_name || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* File Upload Area */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            {selectedFile ? (
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedFile(null)}
                >
                  Change File
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Upload className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-primary mb-1">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PDF files only (Max 10MB)
                  </p>
                </div>
                <label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button variant="outline" size="sm" asChild>
                    <span>Select PDF File</span>
                  </Button>
                </label>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-xs text-muted-foreground">
              <strong>Note:</strong> Please ensure the discharge summary is complete and signed 
              before uploading. This document will be shared with the care team.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="bg-primary hover:bg-primary/90"
          >
            {uploading ? (
              <>
                <Upload className="w-4 h-4 mr-2 animate-pulse" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload Summary
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
